import { getFeatureFlags, useFeatureFlags } from '@/config/featureFlags';

import {
  doc,
  onSnapshot,
  Timestamp,
  Unsubscribe,
} from 'firebase/firestore';
import { create } from 'zustand';

import { db } from '@/config';

export type featuresAppType = 'MUSEUM' | 'PLAYER';
/** Dostawcy PMS/integracji włączeni dla obiektu — patrz `enabledIntegrations` niżej. */
export type IntegrationProviderType = 'opera';
export type PlanType = 'TEST' | 'BASE' | 'PRO' | 'ULTIMATE' | 'CUSTOM' | 'INDIVIDUAL';

export interface OrganizationState {
  // Organization data
  organizationId: string | null;
  code: string | null;
  webAppUrl: string | null;
  featuresApp: featuresAppType[] | null;
  languages: string[] | null;
  defaultLang: string;
  accountManager: string | null;
  owner: string | null;

  /**
   * Organizacja wyłączona przez administratora HotelSpot
   * (`organizations/{id}.isDisabled`). Panel zostawia wtedy nawigację, ale
   * zamiast treści widoku pokazuje komunikat o blokadzie. Wartość idzie tym
   * samym `onSnapshot` co reszta organizacji, więc przełączenie flagi w bazie
   * działa na żywo, bez przeładowania.
   */
  isDisabled: boolean;

  // Mobile App
  mobileAppAvailable: boolean;

  /**
   * Czy obiekt może korzystać z płatności online — lustro `hotels/{id}.stripePaymentsAvailable`.
   *
   * DOMYŚLNIE TAK: brak pola znaczy „może", bo hotel włącza sobie Stripe sam w
   * Ustawienia → Integracje. Jawne `false` (ustawiane wyłącznie z hs-admina) odbiera
   * dostęp i chowa kafelek. Sama zgoda hotelu, czyli regulamin i włączenie, siedzi
   * w `integrations/stripe` i czytamy ją przez backend.
   */
  stripePaymentsAvailable: boolean;

  /**
   * Obiekt WŁĄCZYŁ płatności online, akceptując regulamin — lustro
   * `hotels/{id}.stripePaymentsEnabled`, pisane przez backend przy zapisie ustawień.
   * Prawda siedzi w `integrations/stripe`, ale ta subkolekcja jest zamknięta dla klienta,
   * a menu musi znać odpowiedź, zanim cokolwiek się wyrenderuje. Domyślnie `false`:
   * obiekt bez włączonych płatności nie ma po co widzieć rejestru transakcji.
   */
  stripePaymentsEnabled: boolean;

  /**
   * Dokument hotelu został już odczytany. Osobno od `isResolved`, bo tamto mówi o
   * dokumencie organizacji, a flagi płatności przychodzą z hotelu, drugim `onSnapshot`.
   * Widok, który na podstawie tych flag przekierowuje, musi umieć odróżnić „wyłączone"
   * od „jeszcze nie wiemy", inaczej wyrzuca z adresu każdego, kto wszedł w link wprost.
   */
  hotelResolved: boolean;

  // Włączone integracje PMS (lustro z `hotels/{id}.enabledIntegrations`, pisane przez
  // backend przy zapisie konfiguracji). Sam config integracji jest zamknięty dla klienta —
  // tutaj mamy wyłącznie listę nazw dostawców, po której panel decyduje, co pokazać w menu.
  enabledIntegrations: IntegrationProviderType[];

  // Push Token Limit
  pushTokenLimit: number;
  pushTokenUsed: number;

  // Subscription plan
  plan: PlanType | null;
  testExpirationDate: Timestamp | null;

  // User's organizations list (from user document)
  organizations: string[] | null;

  // Loading states
  isLoading: boolean;
  isResolved: boolean;

  // Actions
  setOrganizationId: (
    id: string | null,
    userMeta?: { organizations?: string[] | null; testExpirationDate?: Timestamp | null }
  ) => void;
  reset: () => void;
}

const LS_KEYS = {
  ORG_META: 'auth.org.meta',
};

const readLS = <T>(k: string): T | null => {
  try {
    const v = localStorage.getItem(k);
    return v ? (JSON.parse(v) as T) : null;
  } catch {
    return null;
  }
};

const writeLS = (k: string, v: unknown) => {
  try {
    localStorage.setItem(k, JSON.stringify(v));
  } catch {
    /* ignore quota */
  }
};

const initialState = {
  organizationId: null,
  code: null,
  webAppUrl: null,
  featuresApp: null,
  languages: null,
  defaultLang: 'pl',
  accountManager: null,
  owner: null,
  isDisabled: false,
  mobileAppAvailable: false,
  // Flaga startuje wyłączona (Remote Config odpowiada dopiero po chwili), więc i to pole
  // startuje na `false`. Gdy flaga dojdzie, subskrypcja niżej przeliczy je z `rawStripe`.
  stripePaymentsAvailable: false,
  stripePaymentsEnabled: false,
  hotelResolved: false,
  enabledIntegrations: [],
  pushTokenLimit: 100,
  pushTokenUsed: 0,
  plan: null,
  testExpirationDate: null,
  organizations: null,
  isLoading: false,
  isResolved: false,
};

let orgUnsub: Unsubscribe | null = null;
let hotelUnsub: Unsubscribe | null = null;
// @ts-ignore
let storageListener: (e: StorageEvent) => void = null;

const clearOrgListeners = () => {
  if (orgUnsub) {
    orgUnsub();
    orgUnsub = null;
  }
  if (hotelUnsub) {
    hotelUnsub();
    hotelUnsub = null;
  }
  if (storageListener) {
    window.removeEventListener('storage', storageListener);
    // @ts-ignore
    storageListener = null;
  }
};

/**
 * Ostatnie SUROWE wartości z Firestore (bez bramkowania flagą).
 *
 * Potrzebne, bo Remote Config odpowiada asynchronicznie i zwykle PO tym, jak snapshot
 * hotelu już przeleciał. Bez zapamiętania surowych wartości nie dałoby się przeliczyć
 * bramkowania w momencie, w którym flaga dojdzie — trzeba by czekać na kolejny snapshot.
 */
const rawStripe = { available: true, enabled: false };

/** Zapisuje surowe wartości i zwraca je już zbramkowane flagą. */
const rememberStripe = (available: boolean, enabled: boolean) => {
  rawStripe.available = available;
  rawStripe.enabled = enabled;
  return gatedStripe();
};

/** Surowe wartości przez filtr flagi wydania. */
const gatedStripe = () => {
  const { stripeEnable } = getFeatureFlags();
  return {
    available: stripeEnable && rawStripe.available,
    enabled: stripeEnable && rawStripe.enabled,
  };
};

export const useOrganizationState = create<OrganizationState>((set, get) => ({
  ...initialState,

  setOrganizationId: (id: string | null, userMeta) => {
    const currentId = get().organizationId;

    // Jeśli ta sama organizacja i nie ma userMeta (nie jest to zmiana z selektora), nic nie rób
    // Jeśli jest userMeta, znaczy że to jawna zmiana z selektora i powinniśmy odświeżyć
    if (currentId === id && !userMeta) return;

    clearOrgListeners();

    if (!id) {
      set({
        ...initialState,
        isResolved: true,
      });
      writeLS(LS_KEYS.ORG_META, {});
      return;
    }

    // Ustaw nową organizację, user meta i załaduj cache
    set({
      organizationId: id,
      isLoading: true,
      organizations: userMeta?.organizations ?? null,
      testExpirationDate: userMeta?.testExpirationDate ?? null,
    });

    // Zapisz ID organizacji do localStorage dla przywracania przy kolejnym logowaniu
    try {
      localStorage.setItem('auth.lastOrgId', id);
    } catch {
      /* ignore quota */
    }

    const cachedOrg = readLS<Partial<OrganizationState> & { testExpirationMs?: number | null }>(LS_KEYS.ORG_META);

    if (cachedOrg) {
      // Restore testExpirationDate from cached milliseconds
      if (cachedOrg.testExpirationMs) {
        cachedOrg.testExpirationDate = Timestamp.fromMillis(cachedOrg.testExpirationMs);
      }
      delete cachedOrg.testExpirationMs;
      set(cachedOrg);
    }

    // Dodaj listener na zmiany localStorage z innych zakładek
    storageListener = (e: StorageEvent) => {
      if (e.key === LS_KEYS.ORG_META && e.newValue && e.oldValue !== e.newValue) {
        try {
          const updatedCache = JSON.parse(e.newValue);
          if (updatedCache && updatedCache.code !== undefined) {
            // Liczone PRZED literałem obiektu: `rememberStripe` ma efekt uboczny (zapisuje
            // surowe wartości), więc poleganie na kolejności wyliczania pól byłoby kruche.
            const stripeFromCache = rememberStripe(
              updatedCache.stripePaymentsAvailable !== false,
              updatedCache.stripePaymentsEnabled === true,
            );
            const cacheData: Partial<OrganizationState> = {
              code: updatedCache.code,
              webAppUrl: updatedCache.webAppUrl,
              featuresApp: Array.isArray(updatedCache.featuresApp) ? updatedCache.featuresApp : null,
              languages: updatedCache.languages,
              defaultLang: updatedCache.defaultLang,
              accountManager: updatedCache.accountManager,
              owner: updatedCache.owner,
              mobileAppAvailable: updatedCache.mobileAppAvailable ?? false,
              stripePaymentsAvailable: stripeFromCache.available,
              stripePaymentsEnabled: stripeFromCache.enabled,
              enabledIntegrations: Array.isArray(updatedCache.enabledIntegrations) ? updatedCache.enabledIntegrations : [],
              pushTokenLimit: updatedCache.pushTokenLimit,
              pushTokenUsed: updatedCache.pushTokenUsed,
              plan: updatedCache.plan,
            };

            if (updatedCache.testExpirationMs) {
              cacheData.testExpirationDate = Timestamp.fromMillis(updatedCache.testExpirationMs);
            }

            set(cacheData);
          }
        } catch (error) {
          // Ignoruj błędy parsowania
        }
      }
    };
    window.addEventListener('storage', storageListener);

    // Zasubskrybuj organization snapshot
    const orgRef = doc(db, 'organizations', id);
    orgUnsub = onSnapshot(
      orgRef,
      (orgSnap) => {
        if (!orgSnap.exists()) {
          // Reset organizationId when document doesn't exist
          set({
            ...initialState,
            organizationId: null,
            isResolved: true,
            isLoading: false,
          });
          return;
        }

        const o = orgSnap.data() as any;

        const orgData: Partial<OrganizationState> = {
          code: o.code ?? null,
          webAppUrl: o.webAppUrl ?? null,
          featuresApp: Array.isArray(o.featuresApp) ? o.featuresApp : null,
          languages: o.languages ?? null,
          defaultLang: o.defaultLang ?? o.default_lang ?? o.languages?.[0] ?? 'pl',
          accountManager: o.account_manager ?? null,
          owner: o.owner ?? null,
          isDisabled: o.isDisabled === true,
          // mobileAppAvailable will be fetched from hotels collection
          pushTokenLimit: o.pushTokenLimit ?? 100,
          pushTokenUsed: o.pushTokenUsed ?? 0,
          plan: o.plan ?? null,
          testExpirationDate: o.test_expiration_date ?? null,
          isResolved: true,
          isLoading: false,
        };

        set(orgData);
        writeLS(LS_KEYS.ORG_META, {
          code: orgData.code,
          webAppUrl: orgData.webAppUrl,
          featuresApp: orgData.featuresApp,
          languages: orgData.languages,
          defaultLang: orgData.defaultLang,
          accountManager: orgData.accountManager,
          owner: orgData.owner,
          // mobileAppAvailable will be set by hotel listener
          pushTokenLimit: orgData.pushTokenLimit,
          pushTokenUsed: orgData.pushTokenUsed,
          plan: orgData.plan,
          testExpirationMs: orgData.testExpirationDate
            ? (orgData.testExpirationDate as Timestamp).toMillis()
            : null,
        });
      },
      (err) => {
        set({ isResolved: true, isLoading: false });
      },
    );

    // Add listener for hotels collection to get mobileAppAvailable
    const hotelRef = doc(db, 'hotels', id);
    hotelUnsub = onSnapshot(
      hotelRef,
      (hotelSnap) => {
        if (!hotelSnap.exists()) {
          set({ hotelResolved: true });
          return;
        }

        const hotelData = hotelSnap.data();
        const mobileAppAvailable = hotelData?.mobileAppAvailable ?? false;
        // Flaga `stripe_enable` bramkuje OBA pola u źródła, więc kafelek w Ustawieniach,
        // karta integracji, sekcja płatności w punktach, gate regulaminu i pozycja
        // „Płatności" w menu znikają razem — bez dokładania warunku w każdym z tych miejsc.
        const { available: stripePaymentsAvailable, enabled: stripePaymentsEnabled } = rememberStripe(
          hotelData?.stripePaymentsAvailable !== false,
          hotelData?.stripePaymentsEnabled === true,
        );
        const enabledIntegrations: IntegrationProviderType[] = Array.isArray(hotelData?.enabledIntegrations)
          ? hotelData.enabledIntegrations
          : [];

        set({
          mobileAppAvailable, stripePaymentsAvailable, stripePaymentsEnabled, enabledIntegrations, hotelResolved: true,
        });

        // Update localStorage with mobileAppAvailable
        const currentCache = readLS<any>(LS_KEYS.ORG_META) || {};
        writeLS(LS_KEYS.ORG_META, {
          ...currentCache,
          mobileAppAvailable,
          stripePaymentsAvailable,
          stripePaymentsEnabled,
          enabledIntegrations,
        });
      },
      (err) => {
        console.error('Error fetching hotel document:', err);
        // Nie wiemy nic o flagach, ale widok nie może wisieć na loaderze bez końca.
        set({ hotelResolved: true });
      },
    );
  },

  reset: () => {
    clearOrgListeners();
    set({
      ...initialState,
      isResolved: true,
    });
    // Clear organization cache completely
    try {
      localStorage.removeItem(LS_KEYS.ORG_META);
      // Cache listy organizacji (useGetOrganizationsByIds) — czyszczony na logout.
      localStorage.removeItem('auth.orgs.list');
      // Clear any org-related items
      Object.keys(localStorage).forEach((key) => {
        if (key.includes('org') || key.includes('organization')) {
          localStorage.removeItem(key);
        }
      });
    } catch {
      /* ignore */
    }
  },
}));

// Cleanup przy unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', clearOrgListeners);
}

/**
 * Gdy Remote Config dojedzie po snapshocie hotelu, przeliczamy bramkowanie z zapamiętanych
 * surowych wartości. Bez tego interfejs Stripe pojawiłby się dopiero przy następnym
 * odświeżeniu dokumentu hotelu — czyli często wcale.
 */
useFeatureFlags.subscribe(() => {
  const { available, enabled } = gatedStripe();
  const current = useOrganizationState.getState();
  if (current.stripePaymentsAvailable === available && current.stripePaymentsEnabled === enabled) return;
  useOrganizationState.setState({
    stripePaymentsAvailable: available,
    stripePaymentsEnabled: enabled,
  });
});
